import {useUwmHost} from '@uwm/host';
import {type ReactElement, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {Navigate, Route, Routes, useLocation, useNavigate} from 'react-router-dom';

import CalibrationPopup from '@/containers/ExperienceScore/CalibrationPopup/CalibrationPopup';
import ExperienceScoreMain from '@/containers/ExperienceScore/ExperienceScoreMain/ExperienceScoreMain';
import ModalsRoot from '@/containers/Modals/ModalsRoot';
import modalService from '@/services/modal/modalService';
import routerService from '@/services/route/routerService';
import {processUwmCommand} from '@/services/uwm/uwmCommandProcessingService';
import {selectIsCalibrationActive} from '@/state/selectors/inference';
import {selectIsInitialized, selectIsModuleReady} from '@/state/selectors/uwm';

import EntryPage from './containers/EntryPage/EntryPage';
import uwmMessageService from './services/uwm/uwmMessageService';

const RequireReady = ({children}: {children: ReactElement}) => {
    const isReady = useSelector(selectIsModuleReady);

    if (!isReady) return <Navigate to='/' replace />;

    return children;
};

const App = () => {
    useSelector(selectIsInitialized);
    const isModuleReady = useSelector(selectIsModuleReady);
    const isCalibrationActive = useSelector(selectIsCalibrationActive);
    const navigate = useNavigate();
    // A layer opens only after the host menu hides; taps on the page in that gap would stack history entries
    const {isAnyOpen} = modalService.parse(useLocation().search);
    routerService.setNavigate(navigate);

    useUwmHost({
        processCommand: processUwmCommand,
        onResize: uwmMessageService.sendResizeMessage,
        onLoaded: uwmMessageService.sendLoadedMessage,
    });

    // Kept above the routes so the screen follows calibration state whenever inference changes, not only on entry.
    useEffect(() => {
        if (!isModuleReady) return;

        navigate(isCalibrationActive ? '/score/calibration' : '/score', {replace: true});
    }, [isModuleReady, isCalibrationActive, navigate]);

    return (
        <>
            {/* .page-slot pairs this screen with the page popup in index.css - the two slide as one swipe. */}
            <div className='page-slot' inert={isAnyOpen}>
                <Routes>
                    <Route path='/' element={<EntryPage />} />
                    <Route
                        path='/score'
                        element={
                            <RequireReady>
                                <ExperienceScoreMain />
                            </RequireReady>
                        }
                    />
                    <Route
                        path='/score/calibration'
                        element={
                            <RequireReady>
                                <CalibrationPopup />
                            </RequireReady>
                        }
                    />
                </Routes>
            </div>
            <ModalsRoot />
        </>
    );
};

export default App;
